import {
  DefaultError,
  MutationFunctionContext,
  MutationKey,
  QueryFilters,
  useMutation as tanstackUseMutation,
  UseMutationOptions,
} from '@tanstack/react-query'
import { DocumentNode } from 'graphql'

import { request } from './client'
import { GraphQlResponse, handleResponse200, ResponseDataShape } from './response'
import { getKey } from './utils'

type Props<Variables extends Record<string, unknown>, ResponseData extends ResponseDataShape> = {
  mutation: DocumentNode
  onCompleted: (data: ResponseData) => Promise<void>
  options?: Omit<
    UseMutationOptions<GraphQlResponse<ResponseData> | void, DefaultError, Variables>,
    'mutationFn'
  >
  queriesToInvalidate?: QueryFilters['queryKey']
}

export const useMutation = <
  Variables extends Record<string, unknown>,
  ResponseData extends ResponseDataShape,
>({
  mutation,
  onCompleted,
  options,
  queriesToInvalidate,
}: Props<Variables, ResponseData>) => {
  type TData = GraphQlResponse<ResponseData> | void
  const key: MutationKey = getKey(mutation)

  return tanstackUseMutation<TData, DefaultError, Variables>({
    mutationFn: async (variables: Variables): Promise<TData> => {
      return request<Variables, ResponseData>({
        key,
        query: mutation,
        variables,
      })
    },
    mutationKey: key,
    onSuccess: (
      response: TData,
      _variables: unknown,
      _onMutateResult: unknown,
      context: MutationFunctionContext,
    ): void => {
      const data: ResponseData | undefined = handleResponse200<ResponseData>({
        response,
      })

      if (data !== undefined) {
        onCompleted(data).finally(async (): Promise<void> => {
          if (queriesToInvalidate !== undefined && queriesToInvalidate.length > 0) {
            await context.client.invalidateQueries({
              queryKey: queriesToInvalidate,
            })
          }
        })
      } else {
        return undefined
      }
    },
    ...options,
  })
}
